import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fileSharingService, Project, FileItem } from '../services/fileSharingService';

interface FileUploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

interface ShareUrlState {
  isGenerating: boolean;
  url: string | null;
  expiresAt: string | null;
  error: string | null;
  expirationStatus?: 'expired' | 'expiring-soon' | 'active';
}

const FileSharingPage: React.FC = () => {
  // State management
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // File upload state
  const [uploadState, setUploadState] = useState<FileUploadState>({
    isUploading: false,
    progress: 0,
    error: null,
  });
  
  // Share URL state per file - with localStorage persistence
  const [shareStates, setShareStates] = useState<Record<string, ShareUrlState>>(() => {
    // Try to load existing states from localStorage
    const savedStates = localStorage.getItem('fileShareStates');
    return savedStates ? JSON.parse(savedStates) : {};
  });
  
  // Save shareStates to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fileShareStates', JSON.stringify(shareStates));
  }, [shareStates]);
  
  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Load files when project changes
  useEffect(() => {
    if (selectedProject) {
      loadProjectFiles(selectedProject.projectId);
    } else {
      setFiles([]);
    }
  }, [selectedProject]);

  // Update share states from loaded files - this ensures we have up-to-date share info after login
  useEffect(() => {
    if (files.length > 0) {
      // Create a new object to track any updates we need to make to shareStates
      const updatedStates: Record<string, ShareUrlState> = {};
      
      files.forEach(file => {
        // If the file has a shareUrl and expirationDate from the server
        if (file.shareUrl && file.expirationDate) {
          // Check what we have in the current state
          const currentState = shareStates[file.fileId];
          
          // If we don't have this in our state, or the URL is different
          if (!currentState?.url || currentState.url !== file.shareUrl) {
            const shareStatus = fileSharingService.checkShareExpiration(file.expirationDate);
            
            updatedStates[file.fileId] = {
              isGenerating: false,
              url: file.shareUrl,
              expiresAt: file.expirationDate,
              error: null,
              expirationStatus: shareStatus.expirationStatus
            };
          }
        }
      });
      
      // If we have updates to make
      if (Object.keys(updatedStates).length > 0) {
        setShareStates(prev => ({
          ...prev,
          ...updatedStates
        }));
      }
    }
  }, [files]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fileSharingService.getProjects();
      setProjects(response.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProjectFiles = async (projectId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fileSharingService.getProjectFiles(projectId);
      setFiles(response.files);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (selectedFiles: FileList | File[]) => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    const fileArray = Array.from(selectedFiles);
    if (fileArray.length === 0) return;

    setUploadState({ isUploading: true, progress: 0, error: null });
    setError(null);

    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const progress = ((i + 1) / fileArray.length) * 100;
        
        setUploadState(prev => ({ ...prev, progress }));
        
        await fileSharingService.uploadFile(selectedProject.projectId, file);
      }
      
      setSuccess(`Successfully uploaded ${fileArray.length} file(s)!`);
      
      // Reload files
      await loadProjectFiles(selectedProject.projectId);
      
    } catch (err) {
      setUploadState(prev => ({ 
        ...prev, 
        error: err instanceof Error ? err.message : 'Upload failed' 
      }));
    } finally {
      setUploadState(prev => ({ ...prev, isUploading: false, progress: 0 }));
    }
  };

  // Check existing share URLs for expiration
  useEffect(() => {
    // For each share state with a URL, check if it's expired
    Object.entries(shareStates).forEach(([fileId, state]) => {
      if (state.url && state.expiresAt) {
        const shareStatus = fileSharingService.checkShareExpiration(state.expiresAt);
        
        if (shareStatus.expirationStatus !== state.expirationStatus) {
          // Update expiration status if it has changed
          setShareStates(prev => ({
            ...prev,
            [fileId]: {
              ...prev[fileId],
              expirationStatus: shareStatus.expirationStatus
            }
          }));
        }
      }
    });
    
    // Check every minute
    const timer = setInterval(() => {
      Object.entries(shareStates).forEach(([fileId, state]) => {
        if (state.url && state.expiresAt) {
          const shareStatus = fileSharingService.checkShareExpiration(state.expiresAt);
          
          if (shareStatus.expirationStatus !== state.expirationStatus) {
            setShareStates(prev => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                expirationStatus: shareStatus.expirationStatus
              }
            }));
          }
        }
      });
    }, 60000); // Check every minute
    
    return () => clearInterval(timer);
  }, [shareStates]);

  const generateShareUrl = async (fileId: string, expirationDays: number = 7) => {
    if (!selectedProject) return;

    setShareStates(prev => ({
      ...prev,
      [fileId]: { isGenerating: true, url: null, expiresAt: null, error: null }
    }));

    try {
      const response = await fileSharingService.generateShareUrl(
        selectedProject.projectId, 
        fileId, 
        expirationDays
      );
      
      // Check expiration status
      const shareStatus = fileSharingService.checkShareExpiration(response.expiresAt);
      
      setShareStates(prev => ({
        ...prev,
        [fileId]: {
          isGenerating: false,
          url: response.downloadUrl,
          expiresAt: response.expiresAt,
          error: null,
          expirationStatus: shareStatus.expirationStatus
        }
      }));
      
    } catch (err) {
      setShareStates(prev => ({
        ...prev,
        [fileId]: {
          isGenerating: false,
          url: null,
          expiresAt: null,
          error: err instanceof Error ? err.message : 'Failed to generate share URL'
        }
      }));
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await fileSharingService.copyToClipboard(text);
      setSuccess('URL copied to clipboard!');
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleFileUpload(droppedFiles);
    }
  }, [selectedProject]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div 
      className="min-h-screen p-4"
      style={{
        backgroundImage: 'url("/images/admin/admin-background-elegant.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Navigation Bar */}
      <nav className="max-w-6xl mx-auto mb-6">
        <div className="flex justify-center gap-2">
          <Link
            to="/"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            🏠 Home
          </Link>
          <Link
            to="/admin/create-project"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            🎨 Create Project
          </Link>
          <Link
            to="/file-sharing"
            className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2 ring-2 ring-blue-500"
          >
            📁 File Sharing
          </Link>
          <Link
            to="/admin"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            ⚙️ Admin
          </Link>
        </div>
      </nav>

      <header className="mb-8 text-center animate-[fadeIn_0.5s_ease-out]">
        <div className="flex justify-center mb-4">
          <img src="/images/admin/ed-character-logo.png" alt="Ed Character Logo" className="h-32" />
        </div>
        <h1 className="text-4xl font-bold mb-2 text-primary drop-shadow-md">File Sharing</h1>
        <p className="text-lg text-muted-foreground mb-4">
          Securely upload and share files with expiring links
        </p>
        <div className="bg-white/70 backdrop-blur-sm py-2 px-6 rounded-full inline-block">
          <p className="text-primary font-medium">
            Upload and manage project files with secure sharing
          </p>
        </div>
      </header>

      {/* Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 animate-[fadeIn_0.3s_ease-out]">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 animate-[fadeIn_0.3s_ease-out]">
          {success}
        </div>
      )}

      {/* Project Selection */}
      <div className="glass-effect rounded-xl shadow-xl p-6 mb-6 border border-white/20 backdrop-blur-md animate-[fadeIn_0.6s_ease-out]">
        <h2 className="text-xl font-semibold text-primary mb-4">Select Project</h2>
        
        {/* Project Selection Dropdown */}
        <div className="mb-4">
          <select
            value={selectedProject?.projectId || ''}
            onChange={(e) => {
              const project = projects.find(p => p.projectId === e.target.value);
              setSelectedProject(project || null);
            }}
            className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 text-lg shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
          >
            <option value="">Select a project...</option>
            {projects.map((project) => (
              <option key={project.projectId} value={project.projectId}>
                {project.projectName} ({project.fileCount || 0} files)
              </option>
            ))}
          </select>
        </div>

        {/* Selected Project Info */}
        {selectedProject && (
          <div className="bg-blue-50/80 rounded-lg p-4">
            <h3 className="font-medium text-blue-900">{selectedProject.projectName}</h3>
            {selectedProject.description && (
              <p className="text-blue-700 text-sm mt-1">{selectedProject.description}</p>
            )}
            <p className="text-blue-600 text-xs mt-2">
              Created: {fileSharingService.formatDate(selectedProject.createdAt)} • 
              Files: {selectedProject.fileCount || 0}
            </p>
          </div>
        )}
      </div>

      {/* File Upload */}
      {selectedProject && (
        <div className="glass-effect rounded-xl shadow-xl p-6 mb-6 border border-white/20 backdrop-blur-md animate-[fadeIn_0.7s_ease-out]">
          <h2 className="text-xl font-semibold text-primary mb-4">Upload Files</h2>
          
          {/* Drag and Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors bg-white/50 ${
              isDragOver
                ? 'border-blue-500 bg-blue-50/80'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="text-4xl mb-4">📁</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Drag and drop files here
            </h3>
            <p className="text-gray-600 mb-4">or</p>
            <label className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium cursor-pointer transition-colors shadow-lg hover:shadow-xl">
              Browse Files
              <input
                type="file"
                multiple
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                className="hidden"
              />
            </label>
          </div>

          {/* Upload Progress */}
          {uploadState.isUploading && (
            <div className="mt-4">
              <div className="bg-blue-50/80 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-900 font-medium">Uploading files...</span>
                  <span className="text-blue-700">{Math.round(uploadState.progress)}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadState.progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {uploadState.error && (
            <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              Upload Error: {uploadState.error}
            </div>
          )}
        </div>
      )}

      {/* File List */}
      {selectedProject && files.length > 0 && (
        <div className="glass-effect rounded-xl shadow-xl p-6 border border-white/20 backdrop-blur-md animate-[fadeIn_0.8s_ease-out]">
          <h2 className="text-xl font-semibold text-primary mb-4">
            Project Files ({files.length})
          </h2>
          
          <div className="bg-white/80 p-4 rounded-lg shadow-inner">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-700">File</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Size</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Uploaded</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Access Count</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => {
                    const shareState = shareStates[file.fileId];
                    return (
                      <tr key={file.fileId} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <div className="flex items-center">
                            <span className="text-xl mr-3">
                              {fileSharingService.getFileIcon(file.fileName)}
                            </span>
                            <div>
                              <div className="font-medium text-gray-900">{file.fileName}</div>
                              <div className="text-gray-500 text-xs">{file.contentType}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-gray-600">
                          {fileSharingService.formatFileSize(file.fileSize)}
                        </td>
                        <td className="py-3 px-2 text-gray-600">
                          <div>{fileSharingService.formatDate(file.uploadedAt)}</div>
                          <div className="text-xs text-gray-500">by {file.uploadedBy}</div>
                        </td>
                        <td className="py-3 px-2 text-gray-600">
                          {file.accessCount}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex flex-col gap-2">
                            {/* Generate Share URL - Show button if no URL or URL is expired */}
                            {(!shareState?.url || shareState?.expirationStatus === 'expired') && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => generateShareUrl(file.fileId, 7)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-medium"
                                  disabled={shareState?.isGenerating}
                                >
                                  {shareState?.expirationStatus === 'expired' ? 'Regenerate Link (7 days)' : 'Generate Sharing Link'}
                                </button>
                              </div>
                            )}
                            
                            {/* Share URL Display */}
                            {shareState?.url && (
                              <div className={`p-2 rounded text-xs ${
                                shareState.expirationStatus === 'expired' ? 'bg-red-50' : 
                                shareState.expirationStatus === 'expiring-soon' ? 'bg-yellow-50' : 
                                'bg-green-50'
                              }`}>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-medium ${
                                      shareState.expirationStatus === 'expired' ? 'text-red-600' : 
                                      shareState.expirationStatus === 'expiring-soon' ? 'text-yellow-600' : 
                                      'text-green-600'
                                    }`}>Share URL:</span>
                                    {shareState.expirationStatus !== 'expired' && (
                                      <button
                                        onClick={() => copyToClipboard(shareState.url!)}
                                        className="text-blue-600 hover:text-blue-800"
                                        title="Copy to clipboard"
                                      >
                                        📋
                                      </button>
                                    )}
                                  </div>
                                  {/* Expiration status badge */}
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    shareState.expirationStatus === 'expired' ? 'bg-red-100 text-red-800' : 
                                    shareState.expirationStatus === 'expiring-soon' ? 'bg-yellow-100 text-yellow-800' : 
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {shareState.expirationStatus === 'expired' ? 'Expired' : 
                                     shareState.expirationStatus === 'expiring-soon' ? 'Expiring Soon' : 
                                     'Active'}
                                  </span>
                                </div>
                                
                                <div className={`break-all ${
                                  shareState.expirationStatus === 'expired' ? 'text-red-700 line-through opacity-70' : 
                                  shareState.expirationStatus === 'expiring-soon' ? 'text-yellow-700' : 
                                  'text-green-700'
                                }`}>
                                  {shareState.url}
                                </div>
                                
                                <div className={`mt-1 ${
                                  shareState.expirationStatus === 'expired' ? 'text-red-600' : 
                                  shareState.expirationStatus === 'expiring-soon' ? 'text-yellow-600' : 
                                  'text-green-600'
                                }`}>
                                  {shareState.expirationStatus === 'expired' ? (
                                    <div className="flex items-center justify-between">
                                      <span>Expired on {fileSharingService.formatDate(shareState.expiresAt!)}</span>
                                      <button 
                                        onClick={() => generateShareUrl(file.fileId, 7)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-medium"
                                      >
                                        Regenerate
                                      </button>
                                    </div>
                                  ) : shareState.expirationStatus === 'expiring-soon' ? (
                                    <div className="flex items-center justify-between">
                                      <span>Expires soon: {fileSharingService.formatDate(shareState.expiresAt!)}</span>
                                      <button 
                                        onClick={() => generateShareUrl(file.fileId, 7)}
                                        className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-2 py-1 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-medium"
                                      >
                                        Extend
                                      </button>
                                    </div>
                                  ) : (
                                    <span>Expires: {fileSharingService.formatDate(shareState.expiresAt!)}</span>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {shareState?.error && (
                              <div className="text-red-600 text-xs">{shareState.error}</div>
                            )}
                            
                            {shareState?.isGenerating && (
                              <div className="text-blue-600 text-xs">Generating...</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {selectedProject && files.length === 0 && !isLoading && (
        <div className="glass-effect rounded-xl shadow-xl p-12 text-center border border-white/20 backdrop-blur-md">
          <div className="text-6xl mb-4">📄</div>
          <h3 className="text-xl font-medium text-primary mb-2">No files yet</h3>
          <p className="text-muted-foreground">Upload your first file to get started</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center p-8 glass-effect rounded-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-white font-medium drop-shadow-md">Loading...</p>
        </div>
      )}
    </div>
  );
};

export default FileSharingPage;
